const maxReportsPerPage = 20;
const maxEmailStackFrames = 30;
const defaultFrontendLoggingEmail = 'ua4work@gmail.com';

function getFrontendLoggingEmail() {
  if (!document.kvConfig) {
    return defaultFrontendLoggingEmail;
  }

  return document.kvConfig.frontendLoggingEmail || defaultFrontendLoggingEmail;
}

function buildEmailLogContext(payload) {
  const kvConfig = payload.kvConfig || {};

  return {
    app: payload.app,
    userId: kvConfig.userId,
    jobId: kvConfig.jobId,
    visualizerType: kvConfig.visualizerType,
    navigationType: kvConfig.navigationType,
    toolVersion: kvConfig.toolVersion,
    isBackend: kvConfig.isBackend,
    url: payload.url,
    browser: payload.browser,
    timestamp: payload.timestamp,
  };
}

// The single-line summary is only needed for the email alert's headline, so
// it's built here rather than carried as a redundant field on every payload
// (Loki/Elasticsearch already get type/message/url plus the full kvConfig as
// separate, individually filterable fields).
function buildEmailSummary(payload) {
  const kvConfig = payload.kvConfig || {};
  const userId = kvConfig.userId || 'unknown-user';
  const visualizerType = kvConfig.visualizerType || 'unknown-visualizer';
  const message = payload.message || 'Unknown frontend error';

  return payload.type + ' | ' + message + ' | userId=' + userId + ' | visualizer=' + visualizerType + ' | ' + payload.url;
}

function parseStackFrame(frame) {
  const chromeFrameWithFunction = frame.match(/^at\s+(.+?)\s+\((.+):(\d+):(\d+)\)$/);

  if (chromeFrameWithFunction) {
    return {
      raw: frame,
      functionName: chromeFrameWithFunction[1],
      file: chromeFrameWithFunction[2],
      line: Number(chromeFrameWithFunction[3]),
      column: Number(chromeFrameWithFunction[4]),
    };
  }

  const chromeFrameWithoutFunction = frame.match(/^at\s+(.+):(\d+):(\d+)$/);

  if (chromeFrameWithoutFunction) {
    return {
      raw: frame,
      functionName: null,
      file: chromeFrameWithoutFunction[1],
      line: Number(chromeFrameWithoutFunction[2]),
      column: Number(chromeFrameWithoutFunction[3]),
    };
  }

  const firefoxFrame = frame.match(/^(.*?)@(.+):(\d+):(\d+)$/);

  if (firefoxFrame) {
    return {
      raw: frame,
      functionName: firefoxFrame[1] || null,
      file: firefoxFrame[2],
      line: Number(firefoxFrame[3]),
      column: Number(firefoxFrame[4]),
    };
  }

  return {
    raw: frame,
  };
}

function splitStackFrames(stack, dropErrorHeading = false) {
  if (!stack) {
    return [];
  }

  const frames = String(stack)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (dropErrorHeading && frames.length && frames[0].indexOf('at ') !== 0 && frames[0].indexOf('@') === -1) {
    return frames.slice(1).map(parseStackFrame);
  }

  return frames.map(parseStackFrame);
}

function buildEmailLogStackTrace(payload) {
  const context = buildEmailLogContext(payload);
  const allStackFrames = splitStackFrames(payload.stack, true);
  const allReactFrames = splitStackFrames(payload.reactStack);
  const stackFrames = allStackFrames.slice(0, maxEmailStackFrames);
  const reactFrames = allReactFrames.slice(0, maxEmailStackFrames);
  const stackTrace = {
    error: {
      type: payload.type,
      level: payload.level,
      name: payload.name,
      message: payload.message || 'Unknown frontend error',
      source: payload.source,
      line: payload.line,
      column: payload.column,
    },
    http: payload.http,
    topFrame: stackFrames[0] || null,
    stackFrames,
    reactFrames,
    framesTruncated: {
      stackFrames: allStackFrames.length > stackFrames.length,
      reactFrames: allReactFrames.length > reactFrames.length,
    },
    context,
  };

  return JSON.stringify(stackTrace, null, 2);
}

export function buildEmailLogPayload(payload) {
  const body = {
    message: buildEmailSummary(payload),
    stackTrace: buildEmailLogStackTrace(payload),
  };
  const email = getFrontendLoggingEmail();

  if (email) {
    body.email = email;
  }

  return body;
}

export function createErrorTransport(endpointUrl, options = {}) {
  const fetchBeforeInstrumentation = window.fetch;
  let reportsSent = 0;

  function sendWithFetch(body) {
    const fetchFn = fetchBeforeInstrumentation || window.fetch;

    if (!fetchFn) {
      return false;
    }

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
      keepalive: true,
    };

    if (options.credentials) {
      requestOptions.credentials = options.credentials;
    }

    fetchFn.call(window, endpointUrl, requestOptions).catch(() => {});

    return true;
  }

  function sendWithXhr(body) {
    const request = new XMLHttpRequest();

    request.open('POST', endpointUrl, true);
    request.setRequestHeader('Content-Type', 'application/json');
    request.send(body);
  }

  return {
    send(payload) {
      if (reportsSent >= maxReportsPerPage) {
        return;
      }

      reportsSent += 1;
      const payloadBody = options.formatPayload ? options.formatPayload(payload) : payload;
      const body = JSON.stringify(payloadBody);

      if (options.useBeacon !== false && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        const sent = navigator.sendBeacon(endpointUrl, blob);

        if (sent) {
          return;
        }
      }

      if (sendWithFetch(body)) {
        return;
      }

      sendWithXhr(body);
    },
  };
}
